import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { contentApi } from "../api";
import type { RelatedKind, RelatedOption } from "../types";

const PLACEHOLDER: Record<RelatedKind, string> = {
  artist: "Busca un artista por nombre o id",
  event: "Busca un evento por nombre o id",
  club: "Busca un club por nombre o id",
};

/**
 * Elige el artista, evento o club del que habla la pieza. En la app aparece en
 * «En Mixcore» y lleva a su página. Busca por nombre o por id.
 */
export function RelatedPicker({
  kind,
  label,
  value,
  onChange,
  onResolved,
}: {
  kind: RelatedKind;
  label: string;
  value: number | null | undefined;
  onChange: (id: number | null, option?: RelatedOption) => void;
  onResolved?: (option: RelatedOption | null) => void;
}) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Etiqueta del valor guardado (sólo trae el id).
  const current = useQuery({
    queryKey: ["content", "related", kind, "id", value],
    queryFn: () => contentApi.relatedSearch(kind, String(value)),
    enabled: !!value,
    staleTime: 5 * 60_000,
  });
  const selected = value ? current.data?.find((o) => o.id === value) ?? null : null;
  useEffect(() => {
    onResolved?.(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, selected?.label]);

  const results = useQuery({
    queryKey: ["content", "related", kind, "q", debounced],
    queryFn: () => contentApi.relatedSearch(kind, debounced),
    enabled: open,
    staleTime: 60_000,
  });

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-fg-muted">{label}</span>
      {value ? (
        <div className="flex items-center gap-2 rounded-md border border-border-subtle bg-bg-subtle px-2 py-1.5">
          {selected?.image ? <img src={selected.image} alt="" className="h-7 w-7 rounded object-cover" /> : null}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm text-fg">{selected?.label ?? (current.isLoading ? "Cargando…" : `#${value}`)}</div>
            {selected?.sublabel ? <div className="truncate text-2xs text-fg-subtle">{selected.sublabel}</div> : null}
          </div>
          <button
            type="button"
            title="Quitar"
            className="rounded p-1 text-fg-muted hover:bg-bg-hover hover:text-fg"
            onClick={() => onChange(null)}
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle" />
          <input
            className="nx-input pl-8"
            value={q}
            placeholder={PLACEHOLDER[kind]}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
          />
          {open && (
            <div className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border-subtle bg-bg-base shadow-lg">
              {results.isLoading ? (
                <div className="px-3 py-2 text-xs text-fg-subtle">Buscando…</div>
              ) : results.isError ? (
                <div className="px-3 py-2 text-xs text-danger">No se pudo buscar.</div>
              ) : (results.data ?? []).length === 0 ? (
                <div className="px-3 py-2 text-xs text-fg-subtle">Sin resultados.</div>
              ) : (
                (results.data ?? []).map((o) => (
                  <button
                    type="button"
                    key={o.id}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-bg-hover"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onChange(o.id, o);
                      setQ("");
                      setOpen(false);
                    }}
                  >
                    {o.image ? <img src={o.image} alt="" className="h-7 w-7 rounded object-cover" /> : <div className="h-7 w-7 rounded bg-bg-subtle" />}
                    <div className="min-w-0">
                      <div className="truncate text-sm text-fg">{o.label}</div>
                      <div className="truncate text-2xs text-fg-subtle">
                        #{o.id}
                        {o.sublabel ? ` · ${o.sublabel}` : ""}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
